import { useState } from "react";
import type { ViewProps } from "react-native";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import { cn } from "tailwind-variants";
import { WikiEditButton } from "./WikiEditButton";
import { RectButton } from "./RectButton";

export function WikiTitledBox({
  title,
  children,
  bottomCaption,
  contentTestID,
  className,
  headerCustomAction,
  onEditingChange,
  collapsedMaxHeight,
  onLayout,
}: {
  title: string;
  children: React.ReactNode;
  bottomCaption?: string;
  contentTestID?: ViewProps[`testID`];
  className?: ViewProps[`className`];
  headerCustomAction?: React.ReactNode;
  onEditingChange?: (isEditing: boolean) => void;
  collapsedMaxHeight?: number;
  onLayout?: ViewProps[`onLayout`];
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isOverflowExpandable = collapsedMaxHeight != null;
  const headerAction =
    headerCustomAction ??
    (onEditingChange == null ? null : (
      <WikiEditButton
        editing={isEditing}
        onPress={() => {
          const next = !isEditing;
          setIsEditing(next);
          onEditingChange(next);
        }}
      />
    ));

  return (
    <View className={cn(`gap-2`, className)} onLayout={onLayout}>
      <View className="flex-row items-center">
        <Text className="flex-1 font-sans text-sm/normal font-semibold text-muted-fg uppercase">
          {title}
        </Text>
        {headerAction}
      </View>

      <View
        className={`
          relative overflow-hidden rounded-lg bg-bg-high outline -outline-offset-1

          ${isEditing ? `outline-2 outline-blue` : `outline-fg/10`}
        `}
      >
        {isOverflowExpandable ? (
          <>
            {isExpanded ? null : (
              <>
                <View
                  className={`
                    pointer-events-none absolute inset-x-0 bottom-2 z-10 h-20
                    [mask-image:linear-gradient(to_bottom,transparent,black_45%,black)]
                    backdrop-blur-sm
                  `}
                />
                <View className="absolute inset-x-0 bottom-2 z-10 items-center">
                  <RectButton
                    variant="bareDim"
                    iconStart="chevron-down-circled"
                    onPress={() => {
                      setIsExpanded(true);
                    }}
                  >
                    Expand
                  </RectButton>
                </View>
              </>
            )}

            <View className="relative">
              <View
                testID={contentTestID}
                className={`
                  overflow-hidden rounded-t-lg

                  ${
                    isExpanded
                      ? ``
                      : `[mask-image:linear-gradient(to_bottom,black,black_55%,transparent)]`
                  }
                `}
                style={
                  isExpanded ? undefined : { maxHeight: collapsedMaxHeight }
                }
              >
                {children}
              </View>
            </View>

            {isExpanded ? (
              <View className="px-2 pt-1 pb-2">
                <RectButton
                  variant="bareDim"
                  iconStart="chevron-up-circled"
                  onPress={() => {
                    setIsExpanded(false);
                  }}
                >
                  Collapse
                </RectButton>
              </View>
            ) : null}
          </>
        ) : (
          <View testID={contentTestID}>{children}</View>
        )}
      </View>

      {bottomCaption == null ? null : (
        <Text className="pyly-body-caption text-muted-fg">{bottomCaption}</Text>
      )}
    </View>
  );
}
